/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { CloseIcon as LodestarCloseIcon } from "@smartsheet/lodestar-icons";
import { CloseIcon as UrsaCloseIcon } from "@smartsheet/ursa-icons";
import React, { FC, HTMLAttributes } from "react";
import { useTheme } from "../../../theme/utils/useTheme";
import { StyledModalActionSeparator } from "../styled/ActionSeparator";
import { StyledModalActions } from "../styled/Actions";
import { StyledModalHeader } from "../styled/Header";
import { StyledModalTitle } from "../styled/Title";
import { StyledTitleIcon } from "../styled/TitleIcon";
import { StyledTitleText } from "../styled/TitleText";
import { ModalType } from "../types";
import { ModalActionButton } from "./ModalActionButton";
import { XButtonProps } from "./XButton";

export interface ModalHeaderProps {
    /**
     * the aria-label attribute associated with the modal's close button. Defaults to "Close" if not explicitly set.
     */
    "aria-label"?: string;
    /**
     * A classname to be applied to the root element of this component.
     */
    className?: string;
    /**
     * Makes the title wrap if necessary
     * If false and the title is longer than one line, the title will elide
     */
    isHeadingMultiline?: boolean;
    /**
     * Elements to be rendered in the modal action slot. Please use `ModalActionButton` here.
     */
    modalActions?: React.ReactNode;
    /**
     * Function that will be called to initiate the exit transition.
     * If not provided, no `x` button will be rendered.
     */
    onCloseRequested?: () => void;
    /**
     * Content to render as the modal title
     */
    title?: React.ReactNode;
    /**
     * An optional icon. Renders to the left of the title
     */
    titleIcon?: React.ReactNode;
    /**
     * Props which should be passed along to the title span
     */
    titleProps?: HTMLAttributes<HTMLSpanElement>;
    /**
     * Type of modal header, helps determine styles and layout. Defaults to "default".
     */
    type?: ModalType;
    /**
     * Pass through props for the button
     */
    xButtonProps?: Partial<XButtonProps>;
}

export const ModalHeader: FC<ModalHeaderProps> = (props) => {
    const {
        isHeadingMultiline = true,
        title,
        titleIcon,
        titleProps,
        modalActions,
        onCloseRequested,
        className,
        "aria-label": ariaLabel = "Close",
        type = "default",
        xButtonProps,
    } = props;
    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    const getActionButtonIcon = () => {
        switch (themeMode) {
            case "ursa-light":
            case "ursa-dark":
                return <UrsaCloseIcon size={24} />;
            case "lodestar-light":
            default:
                return <LodestarCloseIcon />;
        }
    };

    return (
        <StyledModalHeader className={className}>
            <StyledModalTitle variant={type === "simple" ? "headerLarge" : "headlineMedium"} type={type} renderAs="h2">
                {titleIcon && <StyledTitleIcon aria-hidden>{titleIcon}</StyledTitleIcon>}
                <StyledTitleText {...titleProps} type={type} isHeadingMultiline={isHeadingMultiline}>
                    {title}
                </StyledTitleText>
            </StyledModalTitle>
            <StyledModalActions themeMode={themeMode}>
                {modalActions}
                {modalActions && onCloseRequested && (
                    <StyledModalActionSeparator data-client-id="modal-action-separator" />
                )}
                {onCloseRequested && (
                    <ModalActionButton
                        onClick={onCloseRequested}
                        aria-label={ariaLabel}
                        iconBefore={getActionButtonIcon()}
                        {...xButtonProps}
                    />
                )}
            </StyledModalActions>
        </StyledModalHeader>
    );
};
